import { useEffect, useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { ResetPasswordFormSchema, type ResetPasswordForm } from "@/lib/schemas"
import { supabase } from "@/lib/supabase"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { BrandLockup } from "@/components/BrandLockup"

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [isReady, setIsReady] = useState(false)
  const [isExpired, setIsExpired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Listen for PASSWORD_RECOVERY event from Supabase.
  // When the user clicks the reset link in their email, Supabase redirects here
  // with an access token in the URL fragment. The Supabase client (configured with
  // detectSessionInUrl: true) processes the fragment and fires this event.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setIsReady(true)
      }
    })

    // If no PASSWORD_RECOVERY event fires within 5 seconds, the link is likely
    // invalid or the user navigated here directly.
    const timeout = setTimeout(() => {
      setIsExpired((prev) => {
        // Only set expired if still not ready
        if (!isReady) return true
        return prev
      })
    }, 5000)

    return () => {
      subscription.unsubscribe()
      clearTimeout(timeout)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const form = useForm<ResetPasswordForm>({
    resolver: zodResolver(ResetPasswordFormSchema),
    defaultValues: { password: "", confirmPassword: "" },
  })

  const {
    register,
    formState: { errors },
  } = form

  const onSubmit = async (data: ResetPasswordForm) => {
    setIsLoading(true)
    setError(null)
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: data.password,
      })
      if (updateError) throw updateError
      setSuccess(true)
      setTimeout(() => void navigate("/login", { replace: true }), 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset password")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center hero-gradient p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-6">
          <BrandLockup iconSize={24} wordmarkClassName="text-2xl" />
        </div>
        <Card className="p-4">
        <CardHeader className="space-y-1">
          {/* Verifying state */}
          {!isReady && !isExpired && (
            <>
              <div className="flex justify-center py-4">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
              <CardDescription className="text-center">
                Verifying reset link...
              </CardDescription>
            </>
          )}

          {/* Expired / invalid link */}
          {!isReady && isExpired && (
            <>
              <CardTitle className="text-xl text-center">Invalid or expired link</CardTitle>
              <CardDescription className="text-center">
                This password reset link is no longer valid. Please request a new one.
              </CardDescription>
            </>
          )}

          {/* Ready: show form or success */}
          {isReady && !success && (
            <>
              <CardTitle className="text-lg text-center">Set new password</CardTitle>
              <CardDescription className="text-center">
                Choose a new password for your account.
              </CardDescription>
            </>
          )}

          {/* Success state */}
          {success && (
            <>
              <div className="flex justify-center">
                <CheckCircle2 className="h-8 w-8 text-[hsl(var(--status-success))]" />
              </div>
              <CardTitle className="text-lg text-center">Password updated</CardTitle>
              <CardDescription className="text-center">
                Your password has been reset. Redirecting to login...
              </CardDescription>
            </>
          )}
        </CardHeader>

        {/* Expired link actions */}
        {!isReady && isExpired && (
          <CardFooter className="flex flex-col space-y-4">
            <Link to="/forgot-password" className="w-full">
              <Button className="w-full">Request new reset link</Button>
            </Link>
            <Link
              to="/login"
              className="text-sm text-[hsl(var(--brand))] hover:text-[hsl(var(--brand-hover))] hover:underline text-center"
            >
              Back to login
            </Link>
          </CardFooter>
        )}

        {/* Password form */}
        {isReady && !success && (
          <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
            <CardContent className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  autoFocus
                  {...register("password")}
                  className={cn(errors.password && "border-destructive")}
                />
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  {...register("confirmPassword")}
                  className={cn(errors.confirmPassword && "border-destructive")}
                />
                {errors.confirmPassword && (
                  <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
                )}
              </div>
            </CardContent>
            <CardFooter>
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Reset Password
              </Button>
            </CardFooter>
          </form>
        )}
      </Card>
      </div>
    </div>
  )
}
